import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Slider,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FirstPage as FirstPageIcon,
  NavigateBefore as PrevPageIcon,
  NavigateNext as NextPageIcon,
  LastPage as LastPageIcon,
  PictureAsPdf as PdfIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker with fallbacks (version 5.3.93 to match react-pdf)
const workerSrcs = [
  '/pdfjs/pdf.worker.min.js', // Local file (version 5.3.93)
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.3.93/pdf.worker.min.js', // CDN fallback
  'https://unpkg.com/pdfjs-dist@5.3.93/build/pdf.worker.min.js' // Alternative CDN
];

// Try to set up the worker with fallbacks
try {
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrcs[0];
  console.log('PDF.js worker set to local file:', workerSrcs[0]);
  console.log('PDF.js version:', pdfjs.version);
} catch (error) {
  console.warn('Failed to set local PDF worker, trying CDN fallback...');
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrcs[1];
  console.log('PDF.js worker set to CDN fallback:', workerSrcs[1]);
}

interface PDFViewerProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileSize?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  open,
  onClose,
  fileUrl,
  fileName,
  fileSize,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [workerIndex, setWorkerIndex] = useState(0);

  // Reset state when component opens
  useEffect(() => {
    if (open) {
      setPageNumber(1);
      setScale(1.0);
      setNumPages(null);
      setError(null);
      setFullscreen(false);
      setRetryCount(0);
      setWorkerIndex(0);
    }
  }, [open]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    
    // Check for version mismatch error
    if (error.message.includes('API version') && error.message.includes('Worker version')) {
      setError('PDF version mismatch detected. Trying different worker...');
      // Automatically try next worker
      if (workerIndex < workerSrcs.length - 1) {
        setTimeout(() => {
          setWorkerIndex(prev => prev + 1);
          pdfjs.GlobalWorkerOptions.workerSrc = workerSrcs[workerIndex + 1];
          setError(null);
          setLoading(true);
        }, 1000);
        return;
      }
    }
    
    setError('Error loading PDF: ' + error.message);
    setLoading(false);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    setLoading(true);
    
    // Try next worker if available
    if (workerIndex < workerSrcs.length - 1) {
      setWorkerIndex(prev => prev + 1);
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrcs[workerIndex + 1];
    }
  };

  const onDocumentLoadStart = () => {
    setLoading(true);
    setError(null);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleFirstPage = () => {
    setPageNumber(1);
  };

  const handlePrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const handleLastPage = () => {
    setPageNumber(numPages || 1);
  };

  const handleScaleChange = (_: Event, newValue: number | number[]) => {
    setScale(newValue as number);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : "lg"}
      fullWidth={!fullscreen}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          borderRadius: fullscreen ? 0 : 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          maxHeight: fullscreen ? '100vh' : '90vh',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
                color: 'white',
              }}>
                <PdfIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {fileName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="PDF Document"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                  {fileSize && (
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(fileSize)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton
                  onClick={() => setFullscreen(!fullscreen)}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { 
                      background: 'rgba(0,0,0,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} sx={{ 
                color: 'text.secondary',
                '&:hover': { 
                  background: 'rgba(0,0,0,0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: 'rgba(255, 255, 255, 0.6)' }}>
          {error ? (
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  retryCount < 3 && workerIndex < workerSrcs.length - 1 ? (
                    <Button color="inherit" size="small" onClick={handleRetry}>
                      Try Different Worker
                    </Button>
                  ) : retryCount < 3 ? (
                    <Button color="inherit" size="small" onClick={handleRetry}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {error}
                {retryCount >= 3 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Maximum retry attempts reached. Please try downloading the file instead.
                  </Typography>
                )}
              </Alert>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              {/* PDF Controls */}
              <Paper sx={{ 
                p: 2, 
                mb: 2, 
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  {/* Page Navigation */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="First Page">
                      <IconButton 
                        onClick={handleFirstPage}
                        disabled={pageNumber === 1}
                        size="small"
                      >
                        <FirstPageIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Previous Page">
                      <IconButton 
                        onClick={handlePrevPage}
                        disabled={pageNumber === 1}
                        size="small"
                      >
                        <PrevPageIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" sx={{ minWidth: '80px', textAlign: 'center' }}>
                      {pageNumber} / {numPages || '...'}
                    </Typography>
                    <Tooltip title="Next Page">
                      <IconButton 
                        onClick={handleNextPage}
                        disabled={pageNumber === numPages}
                        size="small"
                      >
                        <NextPageIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Last Page">
                      <IconButton 
                        onClick={handleLastPage}
                        disabled={pageNumber === numPages}
                        size="small"
                      >
                        <LastPageIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Zoom Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                    <Tooltip title="Zoom Out">
                      <IconButton onClick={handleZoomOut} size="small">
                        <ZoomOutIcon />
                      </IconButton>
                    </Tooltip>
                    <Slider
                      value={scale}
                      onChange={handleScaleChange}
                      min={0.5}
                      max={3.0}
                      step={0.25}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Zoom In">
                      <IconButton onClick={handleZoomIn} size="small">
                        <ZoomInIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" sx={{ minWidth: '40px' }}>
                      {Math.round(scale * 100)}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* PDF Display */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.1)',
                p: 2,
                minHeight: 400
              }}>
                {loading && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 4,
                    gap: 2
                  }}>
                    <CircularProgress size={40} />
                    <Typography variant="body1">
                      Loading PDF...
                    </Typography>
                  </Box>
                )}
                
                <Document
                  key={`pdf-${retryCount}-${workerIndex}`}
                  file={fileUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  onLoadStart={onDocumentLoadStart}
                  loading={
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      py: 4,
                      gap: 2
                    }}>
                      <CircularProgress size={40} />
                      <Typography variant="body1">
                        Loading PDF...
                      </Typography>
                    </Box>
                  }
                  error={
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      py: 4,
                      gap: 2
                    }}>
                      <Typography variant="body1" color="error">
                        Failed to load PDF
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please try downloading the file instead
                      </Typography>
                    </Box>
                  }
                >
                  <Page 
                    pageNumber={pageNumber} 
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #c62828 30%, #d32f2f 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default PDFViewer;
